/* Data files — load the register and the biometric reports, and pick the
 * period every other tab is drawn from. */

import { useCallback, useRef, useState } from 'react'
import { attendance } from '../../lib/api_attendance'
import type { AttMonth, AttPeriod } from '../../lib/api_attendance'
import { useAsync } from '../../lib/useAsync'
import { useCan } from '../../lib/auth'
import { ErrorBox, LoadElsewhere, Loading, PanelHead } from '../../components/ui'
import { num } from '../../lib/format'
import { BAD, Foot, OK, Sheet, Sub } from './parts'

const KIND: Record<string, string> = { register: 'register', biometric: 'biometric report' }

const loadedOn = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–')

export function DataTab({ D, onPick, onLoaded }: {
  /** The month in view, or null when nothing is on file yet. */
  D: AttMonth | null
  onPick: (month: string) => void
  /** Called after a load, with the month to show. */
  onLoaded: (month: string | undefined) => void
}) {
  // A viewer sees the periods on file; the upload sheet is for editors.
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const { data, error, loading, reload, set } = useAsync(() => attendance.periods(), [])
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ text: string; bad: boolean } | null>(null)
  const [drag, setDrag] = useState(false)
  const [regMonth, setRegMonth] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const reqRef = useRef<HTMLInputElement>(null)

  const periods = data?.periods ?? []
  const count = periods.length === 1 ? 'one period loaded' : `${num(periods.length)} periods loaded`

  const upload = useCallback(async (list: FileList | null) => {
    const files = [...(list ?? [])]
    if (!files.length) return
    const wrong = files.filter((f) => !/\.xlsx$/i.test(f.name))
    if (wrong.length) {
      setStatus({ text: wrong.map((f) => `${f.name}: needs to be an .xlsx report`).join(' · '), bad: true })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const res = await attendance.upload(files, regMonth || undefined)
      const done = res.loaded.map((l) =>
        `${l.filename} — ${KIND[l.kind] ?? l.kind}, ${num(l.rows)} day rows (${num(l.rows_new)} new, ${num(l.rows_updated)} updated), ${l.first_date} to ${l.last_date}`)
      // The parser's own messages already open with the file name.
      const failed = res.failed.map((f) =>
        (f.error.startsWith(f.filename) ? f.error : `${f.filename}: ${f.error}`))
      setStatus({ text: ['Added ' + done.join('; ') + '.', ...failed].join(' · '), bad: failed.length > 0 })
      set({ periods: res.periods })
      // Show the month the last file landed in.
      const last = res.loaded[res.loaded.length - 1]?.last_date
      onLoaded(last ? last.slice(0, 7) : undefined)
    } catch (e: any) {
      setStatus({ text: e.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [regMonth, onLoaded, set])

  const uploadRequirement = useCallback(async (list: FileList | null) => {
    const file = list?.[0]
    if (!file) return
    if (!/\.csv$/i.test(file.name)) {
      setStatus({ text: `${file.name}: save the requirement sheet as CSV and choose that`, bad: true })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const res = await attendance.uploadRequirement(file)
      setStatus({
        text: `Requirement replaced from ${res.source}: ${num(res.workers)} workers and ${num(res.staff)} staff required`
          + (res.warnings.length ? ` · ${res.warnings.join(' · ')}` : '.'),
        bad: res.warnings.length > 0,
      })
      onLoaded(undefined)
    } catch (e: any) {
      setStatus({ text: e.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (reqRef.current) reqRef.current.value = ''
    }
  }, [onLoaded])

  const card = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
    gap: 9, padding: '30px 22px', textAlign: 'center',
  } as const
  const eyebrow = {
    fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase',
    color: 'var(--color-accent-700)',
  } as const
  const blurb = {
    fontSize: 13, color: 'var(--color-neutral-700)', maxWidth: '46ch', lineHeight: 1.5,
  } as const

  return (
    <>
      {canUpload ? <Sheet>
        <PanelHead title="Add a new export" hint={data ? count : undefined} />
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
          gap: 16, marginTop: 18,
        }}>
          <label
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) upload(e.dataTransfer.files) }}
            onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true) }}
            onDragLeave={() => setDrag(false)}
            style={{
              ...card, cursor: busy ? 'progress' : 'pointer',
              border: `1px dashed ${drag ? 'var(--color-accent)' : 'var(--color-divider)'}`,
              background: drag ? 'var(--color-accent-100)' : 'transparent',
            }}>
            <div style={eyebrow}>Sheet 1</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20 }}>Attendance reports</div>
            <div className="pretty" style={blurb}>
              The three .xlsx reports the time office produces — the workers’ register, the
              workers’ biometric report and the staff biometric report — in any order, one or
              several at a time. Which report a file is, is read from its header row, and the
              period from its dates.
            </div>
            <div className="tag tag-outline" style={{ marginTop: 4 }}>
              {busy ? 'Reading…' : 'Choose files'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
              {data ? count : ' '}
            </div>
            <input ref={fileRef} type="file" accept=".xlsx" multiple disabled={busy}
              onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} />
          </label>

          {/* The requirement sheet: one standard, replaced whole by each
              new sheet. */}
          <label style={{ ...card, cursor: busy ? 'default' : 'pointer' }}>
            <div style={eyebrow}>Sheet 2</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20 }}>Workers and staff required</div>
            <div className="pretty" style={blurb}>
              The manpower requirement — heads needed by department, designation and shift, for
              workers and for staff. Every gap on this page is measured against it. A new sheet
              (.csv) replaces the one on file.
            </div>
            <div className="tag tag-outline" style={{ marginTop: 4 }}>
              {busy ? 'Reading…' : 'Choose sheet'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
              {D && D.requirementSheet
                ? `Using ${D.requirementSheet} · ${num(D.requiredTotal)} heads, ${num(D.staff.required)} staff`
                : 'No requirement on file'}
            </div>
            <input ref={reqRef} type="file" accept=".csv" disabled={busy}
              onChange={(e) => uploadRequirement(e.target.files)} style={{ display: 'none' }} />
          </label>
        </div>

        <div className="pretty" style={{
          fontSize: 13, marginTop: 14, minHeight: 20, lineHeight: 1.5,
          color: busy ? 'var(--color-neutral-600)' : status?.bad ? BAD : OK,
        }}>
          {busy ? 'Reading and rebuilding…' : status?.text ?? ''}
        </div>

        <div className="no-print" style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12,
        }}>
          <span className="axis-title">Register month</span>
          <input type="month" className="input" placeholder="YYYY-MM" value={regMonth} disabled={busy}
            onChange={(e) => setRegMonth(e.target.value)} />
          <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
            only needed when the register’s file name does not carry its month
          </span>
        </div>
        <Foot top={0}>
          Load the reports as the time office keeps them, as .xlsx — no need to save them as CSV.
          The register has no dates in it, only day numbers, so its month is read from the file
          name (… SEPT-2026.xlsx) or from the box above. A load writes only the columns its own
          report carries — the register the mark, the biometric the shift, punches and remark — so
          the files can arrive in any order and a corrected one can be loaded again over the
          old; the punch-miss flag is worked out again for the days each load covers. Everything
          stays in the mill’s own database on this machine.
        </Foot>
      </Sheet> : null}
      {loadElsewhere ? <Sheet style={{ padding: '18px 24px' }}><LoadElsewhere /></Sheet> : null}

      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {loading && !data ? <Loading what="the periods on file" /> : null}

      {data ? (
        <Sheet>
          <PanelHead title="Loaded periods" />
          <Sub>Pick one to drive every tab · showing {D ? D.label : '—'}</Sub>
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 14 }}>
            {periods.map((p) => <PeriodRow key={p.month} p={p} showing={p.month === D?.month}
              onPick={() => onPick(p.month)} />)}
            {periods.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-neutral-600)', padding: '13px 4px' }}>
                No attendance on file yet{canUpload ? ' — load the workers’ register above to start a period' : ''}.
              </div>
            ) : null}
          </div>
          <Foot top={16}>
            {D ? (
              <>
                Current source: {D.sourceFile ? D.sourceFile + ' · ' : ''}{num(D.rowCount)} worker-day
                marks · {num(D.register)} workers on the register.{' '}
              </>
            ) : null}
            Every period is read against the one requirement sheet on file
            {D && D.requirementSheet ? ` (${D.requirementSheet})` : ''}, so manning gaps stay comparable month to
            month — and loading a new sheet re-reads every period against it. A period cannot be removed from here; loading a
            corrected report over it updates it in place.
          </Foot>
        </Sheet>
      ) : null}
    </>
  )
}

function PeriodRow({ p, showing, onPick }: { p: AttPeriod; showing: boolean; onPick: () => void }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 110px', gap: 14,
      alignItems: 'start', padding: '13px 4px', borderBottom: '1px solid var(--color-divider)',
    }}>
      <button type="button" onClick={onPick} style={{
        font: 'inherit', textAlign: 'left', background: 'none', border: 0, padding: 0,
        cursor: 'pointer', color: 'var(--color-text)', minWidth: 0,
      }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>{p.label}</div>
        <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 2 }}>
          {num(p.days)} days · {num(p.rows)} worker-day marks
        </div>
        {p.loads.map((l) => (
          <div key={l.id} className="truncate" style={{
            fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 2,
          }}>
            {l.filename ?? 'uploaded'} · {KIND[l.kind] ?? l.kind} · {num(l.rows)} rows · loaded {loadedOn(l.uploaded_at)}
          </div>
        ))}
      </button>
      <div style={{ textAlign: 'right' }}>
        <span className={`tag ${showing ? 'tag-accent' : 'tag-outline'}`}>
          {showing ? 'showing' : 'stored'}
        </span>
      </div>
    </div>
  )
}
